import { onDocumentReady } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/** Must match lazy-loading-in duration in lazy-loading.css */
const ANIMATION_DURATION_MS = 150;

const LAZY_IMAGE_SELECTOR = 'img[loading^="lazy"]:not(.lazy-loaded):not(.lazy-ignore)';

/** @type {MutationObserver | null} */
let lazyLoadingObserver = null;

/** Prevents duplicate load listeners before animation classes are applied. */
const pendingImages = new WeakSet();

/**
 * @param {HTMLImageElement} image
 */
function completeLazyImageAnimation(image) {
  image.classList.add('lazy-loading');

  setTimeout(() => {
    image.classList.add('lazy-loaded');
    image.classList.remove('lazy-loading');
    pendingImages.delete(image);
  }, ANIMATION_DURATION_MS);
}

/**
 * @param {HTMLImageElement} image
 */
function animateLazyImage(image) {
  if (
    image.classList.contains('lazy-loaded') ||
    image.classList.contains('lazy-loading') ||
    pendingImages.has(image)
  ) {
    return;
  }

  pendingImages.add(image);

  if (image.complete && image.naturalHeight !== 0) {
    completeLazyImageAnimation(image);
    return;
  }

  image.addEventListener('load', () => completeLazyImageAnimation(image), { once: true });
}

/**
 * @param {ParentNode} rootEl
 */
function initializeLazyImageAnimation(rootEl = document) {
  if (!('querySelectorAll' in rootEl)) return;

  rootEl.querySelectorAll(LAZY_IMAGE_SELECTOR).forEach((image) => {
    if (image instanceof HTMLImageElement) {
      animateLazyImage(image);
    }
  });
}

/**
 * @param {ParentNode} rootEl
 */
function initializeLazyLoading(rootEl = document) {
  initializeLazyImageAnimation(rootEl);
}

function setupMutationObserver() {
  if (lazyLoadingObserver) return;

  lazyLoadingObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;

      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLImageElement && node.getAttribute('loading')?.startsWith('lazy')) {
          animateLazyImage(node);
        } else if (node instanceof Element || node instanceof DocumentFragment) {
          initializeLazyImageAnimation(node);
        }
      }
    }
  });

  lazyLoadingObserver.observe(document.documentElement, { childList: true, subtree: true });
}

function disconnectLazyLoadingObserver() {
  if (lazyLoadingObserver) {
    lazyLoadingObserver.disconnect();
    lazyLoadingObserver = null;
  }
}

onDocumentReady(() => {
  document.documentElement.classList.add('js');
  initializeLazyLoading();
  setupMutationObserver();
});

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
      initializeLazyLoading(event.target);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    initializeLazyLoading();
  });
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  if (!(event instanceof CustomEvent)) return;

  const html = event.detail?.data?.html;
  if (html && 'querySelectorAll' in html) {
    initializeLazyLoading(html);
  }
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (!(event instanceof CustomEvent)) return;

  const sections = event.detail?.data?.sections;
  if (!sections) return;

  for (const sectionHtml of Object.values(sections)) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sectionHtml;
    initializeLazyLoading(tempDiv);
  }
});

export { initializeLazyLoading, disconnectLazyLoadingObserver };
